import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { COMPANY, inr } from '../mock';
import { proposalsApi, API_BASE } from '../lib/api';
import { ArrowLeft, Download, Share2, Phone, Mail, MapPin, CheckCircle2, Zap, IndianRupee, Leaf, TrendingUp, FileText, Loader2, Eye, Send, Link2, Box, Copy } from 'lucide-react';
import Logo from '../components/Logo';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function ProposalView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: '', cc: '', subject: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await proposalsApi.get(id);
        setP(data);
      } catch (e) {
        setP(null);
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) {
    return <div className="py-20 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading proposal…</div>;
  }
  if (!p) {
    return (
      <div className="max-w-3xl mx-auto text-center py-20">
        <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
        <h2 className="text-xl font-bold text-emerald-950">Proposal not found</h2>
        <p className="text-slate-500 mt-1">It may have been removed.</p>
        <Button className="mt-4 bg-emerald-700 text-white" onClick={() => nav('/app/proposals')}>Back to Proposals</Button>
      </div>
    );
  }

  const download = async () => {
    setDownloading(true);
    try {
      await proposalsApi.downloadPdf(p.id);
      toast.success('PDF downloaded successfully');
    } catch (e) {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };
  const preview = async () => {
    setDownloading(true);
    try {
      await proposalsApi.openPdf(p.id);
    } catch (e) {
      toast.error('Failed to open PDF');
    } finally {
      setDownloading(false);
    }
  };
  const shareWa = () => {
    const link = `${window.location.origin}/share/${p.id}`;
    const msg = encodeURIComponent(`Hi ${p.customer},\n\nHere is your interactive solar proposal from Inpace Power:\n${link}\n\nSystem: ${p.kw} kW\nNet Cost: ${inr(p.netCost)}\nYearly Savings: ${inr(p.yearlySavings)}\nPayback: ${p.payback} yrs`);
    const num = p.phone ? p.phone.replace(/[^0-9]/g, '') : '';
    window.open(`https://wa.me/${num}?text=${msg}`, '_blank');
  };

  const copyShareLink = async () => {
    const link = `${window.location.origin}/share/${p.id}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Share link copied to clipboard');
    } catch (e) {
      toast.error('Could not copy — link is: ' + link);
    }
  };
  const markSent = async () => {
    try {
      const updated = await proposalsApi.update(p.id, { status: 'Sent' });
      setP(updated);
      toast.success('Marked as Sent');
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const openEmail = () => {
    setEmailForm({
      to: p.email || '',
      cc: '',
      subject: `Your Inpace Power Solar Proposal — ${p.id}`,
    });
    setEmailOpen(true);
  };

  const sendEmail = async () => {
    if (!emailForm.to) { toast.error('Customer email is required'); return; }
    setSending(true);
    try {
      await proposalsApi.email(p.id, {
        to: emailForm.to,
        cc: emailForm.cc || undefined,
        subject: emailForm.subject || undefined,
      });
      const updated = await proposalsApi.get(p.id);
      setP(updated);
      toast.success(`Proposal emailed to ${emailForm.to}`);
      setEmailOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-5 no-print flex-wrap">
        <Button variant="ghost" onClick={() => nav('/app/proposals')} className="text-emerald-800"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div className="flex gap-2 flex-wrap">
          {p.status === 'Draft' && (
            <Button variant="outline" onClick={markSent} className="border-emerald-300 text-emerald-800"><CheckCircle2 className="w-4 h-4 mr-2" /> Mark as Sent</Button>
          )}
          <Button variant="outline" onClick={copyShareLink} className="border-emerald-300 text-emerald-800">
            <Link2 className="w-4 h-4 mr-2" /> Copy Share Link
          </Button>
          <Button variant="outline" onClick={preview} disabled={downloading} className="border-emerald-300 text-emerald-800">
            <Eye className="w-4 h-4 mr-2" /> Preview PDF
          </Button>
          <Button variant="outline" onClick={openEmail} className="border-emerald-300 text-emerald-800">
            <Send className="w-4 h-4 mr-2" /> Email to Customer
          </Button>
          <Button variant="outline" onClick={shareWa} className="border-emerald-300 text-emerald-800"><Share2 className="w-4 h-4 mr-2" /> WhatsApp</Button>
          <Button onClick={download} disabled={downloading} className="bg-emerald-700 hover:bg-emerald-800 text-white">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {downloading ? 'Generating…' : 'Download PDF'}
          </Button>
        </div>
      </div>

      {/* Live PDF preview — exactly what the customer will receive */}
      <div className="mb-6 rounded-2xl overflow-hidden border border-emerald-100 shadow-lg bg-white no-print" data-testid="pdf-preview-card">
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-emerald-50 to-amber-50 border-b border-emerald-100">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-700" />
            <div className="text-sm font-bold text-emerald-950">Live PDF Preview</div>
            <span className="text-xs text-slate-500">— this is exactly what your customer will receive</span>
          </div>
          <span className="text-[10px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">12 pages</span>
        </div>
        <object
          data={`${API_BASE}/public/proposals/${p.id}/pdf#view=FitH&toolbar=0`}
          type="application/pdf"
          className="w-full bg-slate-50"
          style={{ height: '720px', border: 'none', display: 'block' }}
          data-testid="pdf-preview-frame"
        >
          <div className="p-6 text-center text-sm text-slate-600">
            Your browser can&apos;t display PDFs inline.{' '}
            <a href={`${API_BASE}/public/proposals/${p.id}/pdf`} target="_blank" rel="noreferrer" className="text-emerald-700 font-semibold underline">
              Open PDF in a new tab
            </a>
          </div>
        </object>
      </div>

      <div className="print-page bg-white rounded-2xl shadow-xl border border-emerald-100 overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-700 to-emerald-900 text-white px-10 py-8 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-60 h-60 rounded-full bg-amber-400/20 blur-2xl" />
          <div className="flex items-start justify-between relative">
            <div>
              <Logo variant="light" />
              <div className="mt-3 text-xs text-emerald-100/80">
                {COMPANY.address}<br />{COMPANY.phone} • {COMPANY.email}<br />GSTIN: {COMPANY.gstin}
              </div>
            </div>
            <div className="text-right">
              <Badge className="bg-amber-400 text-emerald-950 hover:bg-amber-400">SOLAR PROPOSAL</Badge>
              <div className="text-2xl font-extrabold font-mono mt-2">{p.id}</div>
              <div className="text-xs text-emerald-100/80">Issued: {p.date}</div>
            </div>
          </div>
        </div>

        <div className="px-10 py-6 border-b border-emerald-50 grid sm:grid-cols-2 gap-6">
          <div>
            <div className="text-xs font-bold tracking-widest text-emerald-700 uppercase">Prepared For</div>
            <h2 className="mt-1 text-2xl font-extrabold text-emerald-950">{p.customer}</h2>
            <div className="mt-2 space-y-1 text-sm text-slate-600">
              {p.phone && <div className="flex gap-2 items-center"><Phone className="w-3.5 h-3.5" /> {p.phone}</div>}
              {p.email && <div className="flex gap-2 items-center"><Mail className="w-3.5 h-3.5" /> {p.email}</div>}
              <div className="flex gap-2 items-center"><MapPin className="w-3.5 h-3.5" /> {p.address} • {p.segment}</div>
            </div>
          </div>
          <div className="sm:text-right">
            <div className="text-xs font-bold tracking-widest text-emerald-700 uppercase">System Snapshot</div>
            <div className="mt-1 text-5xl font-extrabold text-emerald-900">{p.kw} <span className="text-2xl font-medium text-slate-500">kW</span></div>
            <div className="text-sm text-slate-600 mt-1">{p.numPanels} panels • {p.roofType || 'RCC'} rooftop</div>
          </div>
        </div>

        <div className="px-10 py-7 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { i: Zap, l: 'Yearly Generation', v: (p.yearlyUnits || 0).toLocaleString('en-IN') + ' kWh', c: 'from-amber-100 to-amber-50 text-amber-700' },
            { i: IndianRupee, l: 'Yearly Savings', v: inr(p.yearlySavings || 0), c: 'from-emerald-100 to-emerald-50 text-emerald-700' },
            { i: TrendingUp, l: 'Payback', v: (p.payback || 0) + ' yrs', c: 'from-sky-100 to-sky-50 text-sky-700' },
            { i: Leaf, l: 'CO₂ saved / yr', v: (p.co2 || 0).toLocaleString('en-IN') + ' kg', c: 'from-violet-100 to-violet-50 text-violet-700' },
          ].map(k => {
            const I = k.i;
            return (
              <div key={k.l} className={`p-5 rounded-2xl bg-gradient-to-br ${k.c} border border-white/40`}>
                <I className="w-6 h-6" />
                <div className="mt-3 text-2xl font-extrabold text-emerald-950">{k.v}</div>
                <div className="text-xs text-slate-600">{k.l}</div>
              </div>
            );
          })}
        </div>

        {/* 3D viewer removed — panel count is authoritative from BOM/wizard input */}

        <div className="px-10 py-6 grid lg:grid-cols-2 gap-8 border-t border-emerald-50">
          <div>
            <h3 className="text-lg font-bold text-emerald-950 mb-3">Investment Breakdown</h3>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-emerald-50">
                <tr><td className="py-2.5 text-slate-600">Gross System Cost</td><td className="py-2.5 text-right font-semibold">{inr(p.grossCost)}</td></tr>
                <tr><td className="py-2.5 text-slate-600">Less: Government Subsidy</td><td className="py-2.5 text-right font-semibold text-emerald-700">- {inr(p.subsidy)}</td></tr>
                <tr className="bg-amber-50/60"><td className="py-3 font-bold text-emerald-950">Net Investment</td><td className="py-3 text-right font-extrabold text-emerald-950 text-lg">{inr(p.netCost)}</td></tr>
                <tr><td className="py-2.5 text-slate-600">Yearly Savings</td><td className="py-2.5 text-right font-semibold">{inr(p.yearlySavings)}</td></tr>
                <tr><td className="py-2.5 text-slate-600">25-year Lifetime Savings</td><td className="py-2.5 text-right font-extrabold text-emerald-700">{inr(p.lifetimeSavings)}</td></tr>
              </tbody>
            </table>
          </div>
          <div>
            <h3 className="text-lg font-bold text-emerald-950 mb-3">Bill of Materials</h3>
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-emerald-700 uppercase"><tr><th className="py-2">Item</th><th className="py-2">Qty</th><th className="py-2 text-right">Unit</th></tr></thead>
              <tbody className="divide-y divide-emerald-50">
                <tr><td className="py-2.5">{p.panel?.brand} {p.panel?.model}</td><td className="py-2.5">{p.numPanels}</td><td className="py-2.5 text-right">{inr(p.panel?.price)}</td></tr>
                <tr><td className="py-2.5">{p.inverter?.brand} {p.inverter?.model}</td><td className="py-2.5">{Math.ceil(p.kw / (p.inverter?.kw || 5))}</td><td className="py-2.5 text-right">{inr(p.inverter?.price)}</td></tr>
                <tr><td className="py-2.5">Galvanized MS Mounting Structure</td><td className="py-2.5">{p.kw} kW set</td><td className="py-2.5 text-right">Included</td></tr>
                <tr><td className="py-2.5">DC/AC Cabling & Earthing</td><td className="py-2.5">1 lot</td><td className="py-2.5 text-right">Included</td></tr>
                <tr><td className="py-2.5">Net-Meter & BESCOM liaison</td><td className="py-2.5">1</td><td className="py-2.5 text-right">Included</td></tr>
                <tr><td className="py-2.5">Installation & Commissioning</td><td className="py-2.5">1</td><td className="py-2.5 text-right">Included</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="px-10 py-6 border-t border-emerald-50 bg-emerald-50/40">
          <h3 className="text-lg font-bold text-emerald-950 mb-3">What’s Included</h3>
          <div className="grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm text-slate-700">
            {[
              '25-year linear performance warranty on panels',
              '10-year inverter warranty (extendable)',
              '5-year workmanship warranty by Inpace Power',
              'Free site survey & shading analysis',
              'BESCOM net-metering paperwork',
              '1 year free AMC + remote monitoring',
              'On-call support within 24 hours',
              'Capacity test report on commissioning',
            ].map(t => (
              <div key={t} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> <span>{t}</span></div>
            ))}
          </div>
        </div>

        {p.notes && (
          <div className="px-10 py-5 border-t border-emerald-50">
            <h3 className="text-sm font-bold text-emerald-950 uppercase tracking-wider">Notes</h3>
            <p className="mt-2 text-sm text-slate-600 whitespace-pre-line">{p.notes}</p>
          </div>
        )}

        <div className="px-10 py-7 border-t border-emerald-50 grid sm:grid-cols-2 gap-6">
          <div>
            <div className="text-xs font-bold tracking-widest text-emerald-700 uppercase">Authorized Signatory</div>
            <div className="mt-3 italic text-emerald-900 text-lg" style={{ fontFamily: 'Plus Jakarta Sans' }}>{COMPANY.director}</div>
            <div className="text-xs text-slate-500">Director, Inpace Power</div>
          </div>
          <div className="sm:text-right text-xs text-slate-500">
            This proposal is valid for 30 days from the date of issue. Prices inclusive of GST. Subsidy subject to government norms & customer eligibility.
          </div>
        </div>

        <div className="bg-emerald-950 text-emerald-100 text-center py-3 text-xs">
          Powering Karnataka with clean energy • inpacepowers.com • {COMPANY.phone}
        </div>
      </div>

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle className="text-emerald-950 flex items-center gap-2"><Send className="w-5 h-5 text-emerald-700" /> Email Proposal to Customer</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {p.last_emailed_to && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-xs text-emerald-800">
                Already sent to <strong>{p.last_emailed_to}</strong>{p.last_emailed_at ? ` on ${String(p.last_emailed_at).slice(0,10)}` : ''}.
              </div>
            )}
            <div>
              <Label className="mb-1.5 block">To (customer email) *</Label>
              <Input type="email" value={emailForm.to} onChange={e => setEmailForm(f => ({ ...f, to: e.target.value }))} placeholder="customer@email.com" />
            </div>
            <div>
              <Label className="mb-1.5 block">CC (optional)</Label>
              <Input type="email" value={emailForm.cc} onChange={e => setEmailForm(f => ({ ...f, cc: e.target.value }))} placeholder="director@inpacepowers.com" />
            </div>
            <div>
              <Label className="mb-1.5 block">Subject</Label>
              <Input value={emailForm.subject} onChange={e => setEmailForm(f => ({ ...f, subject: e.target.value }))} />
            </div>
            <div className="text-xs text-slate-500">The proposal PDF will be attached automatically. The email comes from <strong>sales@inpaceenergy.com</strong>.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} disabled={sending}>Cancel</Button>
            <Button onClick={sendEmail} disabled={sending} className="bg-emerald-700 hover:bg-emerald-800 text-white">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {sending ? 'Sending…' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
